import React, { useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  message,
  Row,
  Col,
  Typography,
  Alert,
  Divider,
} from "antd";
import {
  ExclamationCircleOutlined,
  PhoneOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";
import emergencyPropertyService from "../lib/services/emergencyPropertyService";
import Header from '../components/common/CommonHeader';
import Footer from '../components/common/CommonFooter';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EmergencyPropertyPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const urgencyLevels = [
    {
      value: "very_high",
      label: "Very High - Need immediate action",
      color: "#ff0000",
    },
    { value: "high", label: "High - Within 24 hours", color: "#ff4444" },
    { value: "medium", label: "Medium - Within 3 days", color: "#ffaa00" },
    { value: "low", label: "Low - This week", color: "#00aa00" },
  ];

  const propertyTypes = [
    "Apartment",
    "House",
    "Villa",
    "Commercial",
    "Land",
    "Office Space",
    "Shop",
    "Warehouse",
    "Farmhouse",
    "Other",
  ];

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await emergencyPropertyService.createEmergencyProperty(values);
      message.success("Emergency property request submitted successfully!");
      message.info(
        "Our team will contact you within 1 hour for high urgency requests."
      );

      // Reset form
      form.resetFields();

      // Navigate to home after 2 seconds
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      console.error("Error submitting emergency property:", error);
      message.error(
        "Failed to submit emergency property request. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #fff5f5 0%, #fef7ff 100%)",
        minHeight: "100vh",
      }}
    >
      <Header bgColor="#DDC7BB" />
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
        {/* Header */}
        <Card
          style={{
            background: "linear-gradient(135deg, #ff4444, #cc0000)",
            color: "white",
            marginBottom: "24px",
            border: "none",
            boxShadow: "0 8px 25px rgba(255, 0, 0, 0.3)",
          }}
        >
          <div style={{ textAlign: "center", padding: "20px" }}>
            <ExclamationCircleOutlined
              style={{ fontSize: "48px", marginBottom: "16px" }}
            />
            <Title level={1} style={{ color: "white", margin: 0 }}>
              Emergency Property Request
            </Title>
            <Text style={{ color: "white", fontSize: "16px" }}>
              Need to buy or sell property urgently? Fill this form and our team
              will contact you immediately.
            </Text>
          </div>
        </Card>

        {/* Alert */}
        <Alert
          message="Urgent Response Guaranteed"
          description="For 'Very High' urgency levels, our team will contact you within 1 hour. For 'High' urgency, within 24 hours."
          type="warning"
          showIcon
          style={{ marginBottom: "24px" }}
        />

        {/* Form */}
        <Card
          style={{
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            border: "none",
            borderRadius: "12px",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
            size="large"
          >
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Full Name"
                  rules={[
                    { required: true, message: "Please enter your full name" },
                  ]}
                >
                  <Input
                    placeholder="Enter your full name"
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="contact"
                  label="Contact Number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your contact number",
                    },
                    {
                      pattern: /^[0-9+\-\s()]+$/,
                      message: "Please enter a valid contact number",
                    },
                  ]}
                >
                  <Input
                    placeholder="e.g., +1 234 567 8900"
                    prefix={<PhoneOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="address"
              label="Property Address"
              rules={[
                {
                  required: true,
                  message: "Please enter the property address",
                },
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Enter complete property address including city, state, and zip code"
                prefix={<HomeOutlined />}
              />
            </Form.Item>

            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="urgencyLevel"
                  label="Urgency Level"
                  rules={[
                    { required: true, message: "Please select urgency level" },
                  ]}
                >
                  <Select placeholder="Select how urgent your request is">
                    {urgencyLevels.map((level) => (
                      <Option key={level.value} value={level.value}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              background: level.color,
                              marginRight: "8px",
                            }}
                          />
                          {level.label}
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="propertyType"
                  label="Property Type"
                  rules={[
                    { required: true, message: "Please select property type" },
                  ]}
                >
                  <Select placeholder="Select type of property">
                    {propertyTypes.map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="additionalInfo"
              label="Additional Information (Optional)"
            >
              <TextArea
                rows={4}
                placeholder="Please provide any additional details about your emergency property needs, specific requirements, budget range, or timeline..."
              />
            </Form.Item>

            <Divider />

            <Form.Item style={{ textAlign: "center", marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  background: "linear-gradient(135deg, #ff4444, #cc0000)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "0 40px",
                  height: "50px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 15px rgba(255, 0, 0, 0.3)",
                  animation: "pulse 2s infinite",
                }}
              >
                <ExclamationCircleOutlined />
                Submit Emergency Request
              </Button>

              <Button
                type="default"
                onClick={() => navigate("/")}
                size="large"
                style={{
                  marginLeft: "16px",
                  borderRadius: "8px",
                  height: "50px",
                  padding: "0 30px",
                }}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* Additional Info */}
        <Card
          style={{
            marginTop: "24px",
            background: "#f8f9fa",
            border: "none",
            borderRadius: "12px",
          }}
        >
          <Title level={4}>What happens after submission?</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    background: "#ff4444",
                    color: "white",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                    fontWeight: "bold",
                  }}
                >
                  1
                </div>
                <Text strong>Immediate Notification</Text>
                <br />
                <Text type="secondary">Our team gets instant alert</Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    background: "#ffaa00",
                    color: "white",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                    fontWeight: "bold",
                  }}
                >
                  2
                </div>
                <Text strong>Quick Contact</Text>
                <br />
                <Text type="secondary">We call you based on urgency</Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    background: "#00aa00",
                    color: "white",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                    fontWeight: "bold",
                  }}
                >
                  3
                </div>
                <Text strong>Solution Provided</Text>
                <br />
                <Text type="secondary">We help with your property needs</Text>
              </div>
            </Col>
          </Row>
        </Card>
      </div>

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.4);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
            }
          }
        `}
      </style>

      <Footer></Footer>
    </div>
  );
};

export default EmergencyPropertyPage;
